/**
 * packages/ui/react/FileToMatter.jsx — RUN MA STEP 2. ONE control, both filing surfaces.
 *
 * ═════════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT IT CLAIMS, AND THE CLAIM IS THE DESIGN: it ties a CASE to a MATTER.
 *
 * Adam, 2026-08-25: *"We just want it so authorities found in Clark or CiteClerk Research can be tied
 * to a matter and, if a draft is assigned to the same matter, those authorities automatically
 * populate in the authorities list. It has nothing to do with actual answers."*
 *
 * 🔴 IT MUST NOT LET THE COPY IMPLY THE CASE IS NOW IN A DRAFT. Filing attaches no text, ratifies
 * nothing, and does not make the case draftable — ratification is the lawyer's act and it is the
 * moat (`matterSources.js:676`: *"Research fills the shelf; only the lawyer puts a book in the
 * draftable bundle."*). Every string below was written against that sentence.
 *
 * ── WHY IT LIVES IN packages/ui AND NOT TWICE ──────────────────────────────────────────────────
 * Clark is `apps/main`; Research is `apps/steel`. Both already import from `@citeclerk/ui` and both
 * already share `AuthorityCard.jsx` from here. This repo has paid for the alternative more than
 * once — `toParagraphs` and `splitOpinion` were private copies of shared logic, and one of them
 * rendered a 27,999-character opinion as two paragraphs. A filing control implemented twice would
 * drift in exactly the place drift is most expensive: the sentence that tells a lawyer what
 * just happened to their case.
 *
 * The only per-app differences are passed in: `apiBase` ('' inside Research, '/research' from Clark,
 * because Caddy strips that prefix BEFORE the /api auth middleware — RUN LF traced every hop, so
 * this needs no new proxy, no bridge client and no auth work) and `buttonClassName`, so the trigger
 * wears its host's chrome instead of importing a second button style into two design systems.
 *
 * ── THE FOUR STATES ARE RUN LE'S FOUR STATES ───────────────────────────────────────────────────
 * 'loading' | 'ready'+zero | 'ready'+some | 'error', and they are four DIFFERENT screens.
 * 🔴 'zero' IS THE COMMON CASE: RUN LE's census found 5 of the 6 users holding drafts have no steel
 * matter at all. So it is designed first and it gets a DOOR (+ New matter), never an empty dropdown.
 * ⭐ AND 'loading' AND 'error' ARE NOT 'zero'. P1-032: a surface that has not been given something
 * must not report a definite negative about it. "You have no matters" is a claim about the lawyer's
 * account and we may only make it once the account has actually answered.
 *
 * ── THE READ-BACK IS DURABLE, AND READ FROM THE TABLE ──────────────────────────────────────────
 * `GET /matter-authorities/for-case` on mount. Component state dies with the component: without this
 * a case filed yesterday would render an untouched "Send to a matter" and clicking again would tell
 * the lawyer nothing. ⚠️ A FAILED lookup is not "filed nowhere" — it renders as neither.
 *
 * ── EVERY REFUSAL IS RENDERED VERBATIM ─────────────────────────────────────────────────────────
 * 🔴 RUN LG measured the cost of the opposite and it is the reason this file has a `detail` state at
 * all: `AddToMatter.jsx` renders EVERY non-ok response as "Try again", so a precise, permanent
 * server refusal ("this is a statute and filing is keyed to an opinion record") reaches the lawyer
 * as a generic retry prompt they will obey forever. When the body carries `detail`, that sentence is
 * what the lawyer reads. "Try again" appears only when the server said nothing we can quote.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';

/** The one place the two shapes of a good-law value collapse to the string the table stores. */
function goodLawStatus(goodLaw) {
  const s = typeof goodLaw === 'string' ? goodLaw : goodLaw && typeof goodLaw === 'object' ? goodLaw.status : null;
  if (typeof s !== 'string' || !s.trim() || s.trim() === 'unknown') return undefined;
  return s.trim();
}

/**
 * ⭐ THE PARALLEL CITES BECOME ONE STRING, AND THAT IS THE CORRECT REPRESENTATION.
 * A parallel citation genuinely IS one string in Bluebook form — "347 U.S. 483, 74 S. Ct. 686, 98 L.
 * Ed. 873" is how a lawyer writes it, not three separate citations. Tolerates a bare string because
 * Research's opinion payload has `citation` as well as `citations`.
 */
function citeString(cite) {
  if (typeof cite === 'string') return cite.trim() || undefined;
  if (!Array.isArray(cite)) return undefined;
  const seen = new Set();
  const parts = cite.map((c) => (typeof c === 'string' ? c.trim() : '')).filter(Boolean)
    .filter((c) => (seen.has(c) ? false : seen.add(c)));
  return parts.length ? parts.join(', ') : undefined;
}

/** Read a JSON body without letting a non-JSON error page throw. Returns null rather than throwing. */
async function readBody(res) {
  try { return await res.json(); } catch { return null; }
}

/**
 * @param {object}   p
 * @param {string}   p.apiBase        '' inside Research; '/research' from Clark (Caddy strips it).
 * @param {number}   p.clusterId      the opinion cluster. Null/absent → the control refuses honestly.
 * @param {string}   p.caseName       the caption as this surface displays it.
 * @param {string|string[]} p.cite    one cite or the parallel list.
 * @param {string}   p.court
 * @param {number|string} p.year
 * @param {object|string} p.goodLaw   the citator verdict AS HELD. Absent is normal and correct.
 * @param {string}   p.buttonClassName  the host's own button chrome.
 * @param {string}   p.label          the resting label ("Send to a matter" in Clark).
 * @param {boolean}  p.disabled       the host's own gate (e.g. Clark waits for the body to load).
 */
export default function FileToMatter({
  apiBase = '', clusterId = null, caseName = null, cite = null, court = null, year = null,
  goodLaw = null, buttonClassName = '', label = 'Save to a Matter', disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [matters, setMatters] = useState([]);
  const [mattersState, setMattersState] = useState('idle'); // idle | loading | ready | error
  const [mattersDetail, setMattersDetail] = useState(null);

  // The DURABLE read-back: which of my matters hold this case. `null` = not yet known, and that is
  // NOT the same as `[]` = known to be filed nowhere.
  const [filed, setFiled] = useState(null);
  const [action, setAction] = useState('idle'); // idle | sending | error
  const [detail, setDetail] = useState(null);
  const [justFiled, setJustFiled] = useState(null); // { name, already }

  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // ── THE DURABLE READ-BACK ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clusterId) { setFiled([]); return undefined; }
    let cancelled = false;
    setFiled(null);
    fetch(`${apiBase}/api/matter-authorities/for-case?cluster_id=${encodeURIComponent(clusterId)}`,
      { credentials: 'include' })
      .then(async (r) => {
        const d = await readBody(r);
        if (cancelled) return;
        /* 🔴 A FAILED LOOKUP LEAVES `filed` NULL — the button stays in its resting state and claims
           nothing either way. Rendering "not filed" here would be a definite negative about a case
           we did not manage to ask about. */
        if (r.ok && d && d.ok && Array.isArray(d.filed)) setFiled(d.filed);
      })
      .catch(() => { /* leave it unknown — see above */ });
    return () => { cancelled = true; };
  }, [apiBase, clusterId]);

  const loadMatters = useCallback(() => {
    setMattersState('loading');
    setMattersDetail(null);
    fetch(`${apiBase}/api/matter-authorities/matters`, { credentials: 'include' })
      .then(async (r) => {
        const d = await readBody(r);
        // An unusable answer is an ERROR, not an empty list — the two things a screen must never
        // render with the same pixels (C6-0, and RUN LE one component over).
        if (!r.ok || !d || d.ok !== true || !Array.isArray(d.matters)) {
          setMatters([]); setMattersState('error');
          setMattersDetail(d && typeof d.detail === 'string' ? d.detail : null);
          return;
        }
        setMatters(d.matters); setMattersState('ready');
      })
      .catch(() => { setMatters([]); setMattersState('error'); });
  }, [apiBase]);

  const file = useCallback(async (matter) => {
    if (!matter || !clusterId) return;
    setOpen(false); setAction('sending'); setDetail(null);
    try {
      const r = await fetch(`${apiBase}/api/matter-authorities`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          matter_id: matter.id,
          cluster_id: clusterId,
          case_name: caseName || undefined,
          cite: citeString(cite),
          court: court || undefined,
          year: year || undefined,
          good_law: goodLawStatus(goodLaw),
        }),
      });
      const d = await readBody(r);
      if (!r.ok || !d || d.ok !== true) {
        setAction('error');
        /* 🔴 THE SERVER'S OWN SENTENCE, WHEN IT HAS ONE. This is the line RUN LG's brief called
           "small, owed": a refusal a lawyer can act on must not be flattened into "Try again". */
        setDetail(d && typeof d.detail === 'string' ? d.detail : null);
        return;
      }
      setAction('idle');
      setJustFiled({ name: d.matter?.name || matter.name, already: Boolean(d.already) });
      // Re-read the durable state rather than assuming it — the read-back is the point.
      setFiled((prev) => {
        const next = Array.isArray(prev) ? prev.filter((f) => f.matter_id !== matter.id) : [];
        return [{ matter_id: matter.id, matter_name: d.matter?.name || matter.name,
          filed_at: d.authority?.filed_at || null }, ...next];
      });
    } catch {
      setAction('error'); setDetail(null);
    }
  }, [apiBase, clusterId, caseName, cite, court, year, goodLaw]);

  const onPrimary = () => {
    if (action === 'sending') return;
    const next = !open;
    setOpen(next);
    setAction('idle'); setDetail(null);
    if (next && mattersState === 'idle') loadMatters();
  };

  // ── THE RESTING LABEL ───────────────────────────────────────────────────────────────────────
  // ⚠️ It names the MATTER and says only what happened. It does not say "added", "ready" or
  // "in your draft" — none of those are true of a filed authority.
  const filedNames = Array.isArray(filed) ? filed.map((f) => f.matter_name).filter(Boolean) : [];
  /* ⚠️ THE VERB FOLLOWS THE BUTTON. Adam named this control "Save to a Matter" (2026-08-25), so the
     read-back says SAVED — a button that reads "Save" and reports "Filed" is two names for one act,
     and the lawyer has to work out that they are the same thing. The table, the route and this
     file's internals still say `file`; that is INTERNAL vocabulary and no user sees it. */
  const restingLabel =
    action === 'sending' ? 'Saving…'
      : justFiled ? `${justFiled.already ? 'Already in' : 'Saved to'} ${justFiled.name}`
        : filedNames.length === 1 ? `Saved to ${filedNames[0]}`
          : filedNames.length > 1 ? `Saved to ${filedNames.length} matters`
            : label;

  const isFiled = filedNames.length > 0 || Boolean(justFiled);

  return (
    <span className="cc-f2m" ref={wrapRef} data-testid="file-to-matter">
      <button
        type="button"
        className={buttonClassName}
        onClick={onPrimary}
        disabled={disabled || action === 'sending'}
        aria-expanded={open}
        aria-haspopup="menu"
        data-filed={isFiled ? 'true' : 'false'}
        data-testid="file-to-matter-button"
        title={isFiled
          ? `This case is saved to ${filedNames.join(', ') || justFiled?.name}. Saving ties the case to the matter; it does not attach the opinion text.`
          : 'Save this case to one of your matters'}
      >
        <svg className="cc-f2m__ic" viewBox="0 0 24 24" aria-hidden="true">
          {isFiled ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <path d="M12 6v12M6 12h12" />}
        </svg>
        {restingLabel}
      </button>

      {/* 🔴 THE REFUSAL, RENDERED WHERE THE LAWYER IS LOOKING, AND IN THE SERVER'S WORDS.
          ⭐ A CONTAINED NOTICE, NOT A RULE BESIDE THE BUTTON (Adam, 2026-08-25). The first version
          set the sentence loose next to the control with a 2px red rule as its only container, so a
          long server sentence ran away from the button it belonged to and the rule read as debris.
          It is now a small card under the control — same border and radius as the menu, so the two
          things this control can open look like one family — with the critical colour on a DOT
          rather than on the text. Colour still carries no meaning on its own: the text is full ink. */}
      {action === 'error' && (
        <div className="cc-f2m__err" role="alert" data-testid="file-to-matter-error">
          <span className="cc-f2m__dot" aria-hidden="true" />
          <span className="cc-f2m__errtext">{detail || 'The authority couldn’t be saved just now. Try again.'}</span>
        </div>
      )}

      {open && (
        <div className="cc-f2m__menu" role="menu" data-testid="file-to-matter-menu">
          {/* ⭐ THE SCOPE SENTENCE, AT THE MOMENT OF THE DECISION. This is where a lawyer could form
              the belief that filing puts the case into a draft, so this is where it is denied. */}
          <p className="cc-f2m__scope">
            Saves this case to a matter. It doesn’t attach the opinion text or add it to a draft’s
            sources.
          </p>

          {mattersState === 'loading' && (
            <p className="cc-f2m__note" role="status" data-testid="file-to-matter-loading">
              Checking your matters…
            </p>
          )}

          {/* 🔴 THE READ FAILED — WHICH IS NOT "YOU HAVE NO MATTERS". */}
          {mattersState === 'error' && (
            <p className="cc-f2m__note cc-f2m__note--bad" role="alert" data-testid="file-to-matter-matters-error">
              <span className="cc-f2m__dot" aria-hidden="true" />
              <span>
                {mattersDetail || 'We couldn’t check your matters just now.'}{' '}
                <button type="button" className="cc-f2m__link" onClick={loadMatters}>Try again</button>
              </span>
            </p>
          )}

          {/* 🔴 THE COMMON CASE, WITH A DOOR — never an empty dropdown. */}
          {mattersState === 'ready' && matters.length === 0 && (
            <p className="cc-f2m__note" data-testid="file-to-matter-zero">
              No matters yet — there’s nowhere to save this case.{' '}
              <button
                type="button"
                className="cc-f2m__link"
                onClick={() => {
                  setOpen(false);
                  try { window.dispatchEvent(new CustomEvent('cc:open-usage-matters')); } catch { /* no-op */ }
                }}
              >
                + New matter
              </button>
            </p>
          )}

          {mattersState === 'ready' && matters.length > 0 && matters.map((m) => {
            const already = filedNames.length > 0 && Array.isArray(filed)
              && filed.some((f) => f.matter_id === m.id);
            return (
              <button
                key={m.id}
                type="button"
                className="cc-f2m__opt"
                role="menuitem"
                onClick={() => file(m)}
                data-already={already ? 'true' : 'false'}
              >
                <span className="cc-f2m__optname">{m.name}</span>
                {/* Saying it here means the click is never a surprise: the lawyer knows before
                    pressing that this one is already filed, and pressing it is still safe. */}
                {already && <span className="cc-f2m__optnote">already saved</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}
